// CancelVehicleModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, CircularProgress } from '@mui/material';
import { useSchedule } from '../../../hooks/vehicle/useSchedule';
import '../../../assets/scss/components/ui/modal/CancelVehicleModal.scss';

const CancelVehicleModal = ({
  open,
  onClose,
  vehicleInfo,
  operationId,
  onConfirm,
  selectedRouteId = null,
  theme = 'light'
}) => {
  const [formData, setFormData] = useState({
    reason: '',
    replacementType: 'none',
    replacementVehicleId: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Hook personalizado para gestión de datos
  const { useAvailableVehicles } = useSchedule(operationId, selectedRouteId);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        reason: '',
        replacementType: 'none',
        replacementVehicleId: null
      });
      setErrors({});
    }
  }, [open]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Query para obtener vehículos disponibles para reemplazo usando el hook personalizado
  const {
    data: availableVehiclesResponse,
    isLoading: isLoadingVehicles
  } = useAvailableVehicles(vehicleInfo?.date, {
    excludeScheduled: true,
    enabled: open && formData.replacementType === 'manual' && !!vehicleInfo?.date
  });

  // Procesar datos de vehículos disponibles
  const availableVehicles = React.useMemo(() => {
    if (!availableVehiclesResponse?.data) return [];

    // Combinar vehículos regulares y combinados
    const vehicles = availableVehiclesResponse.data.vehicles || [];
    const combined = availableVehiclesResponse.data.combined || [];
    
    return [
      ...vehicles.map(v => ({
        id: v.id,
        label: `${v.plate} - ${v.type}`,
        type: 'vehicle'
      })),
      ...combined.map(c => ({
        id: c.id,
        label: `${c.vehiclePlate} + ${c.trailerPlate}`,
        type: 'combined'
      }))
    ];
  }, [availableVehiclesResponse]);

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    if (!formData.reason.trim()) {
      newErrors.reason = 'La razón es requerida';
    } else if (formData.reason.trim().length < 5) {
      newErrors.reason = 'La razón debe tener al menos 5 caracteres';
    }

    if (formData.replacementType === 'manual' && !formData.replacementVehicleId) {
      newErrors.replacementVehicleId = 'Debe seleccionar un vehículo de reemplazo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpiar error del campo modificado
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Reset vehículo de reemplazo si cambia el tipo
    if (name === 'replacementType' && value !== 'manual') {
      setFormData(prev => ({
        ...prev,
        replacementVehicleId: null
      }));
    }
  };

  // Manejar selección de vehículo de reemplazo
  const handleVehicleSelect = (vehicle) => {
    setFormData(prev => ({
      ...prev,
      replacementVehicleId: vehicle?.id || null
    }));

    if (errors.replacementVehicleId) {
      setErrors(prev => ({
        ...prev,
        replacementVehicleId: undefined
      }));
    }

    setDropdownOpen(false);
  };

  // Manejar envío del formulario
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onConfirm({
        reason: formData.reason.trim(),
        replacementType: formData.replacementType,
        replacementVehicleId: formData.replacementType === 'manual' 
          ? formData.replacementVehicleId 
          : null
      });
    } catch (error) {
      console.error('Error en cancelación:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Obtener información de reemplazo para mostrar
  const getReplacementInfo = () => {
    switch (formData.replacementType) {
      case 'automatic':
        return 'El siguiente vehículo en la lista tomará su lugar automáticamente';
      case 'manual':
        return 'Seleccione el vehículo que tomará su lugar';
      case 'none':
      default:
        return 'No habrá reemplazo. La posición quedará vacía';
    }
  };

  // Obtener vehículo seleccionado
  const getSelectedVehicle = () => {
    return availableVehicles?.find(v => v.id === formData.replacementVehicleId) || null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className={`GM__${theme}-cancel-vehicle-modal`}
    >
      <div className={`GM__${theme}-modal-container`}>
        {/* Header */}
        <div className={`GM__${theme}-modal-header`}>
          <h2 className={`GM__${theme}-modal-title`}>Cancelar Vehículo Programado</h2>
          {vehicleInfo && (
            <p className={`GM__${theme}-modal-subtitle`}>
              {vehicleInfo.plate} - {new Date(vehicleInfo.date).toLocaleDateString('es')}
            </p>
          )}
        </div>

        {/* Content */}
        <div className={`GM__${theme}-modal-content`}>
          <div className={`GM__${theme}-form-container`}>
            {/* Razón de cancelación */}
            <div className={`GM__${theme}-form-group`}>
              <label htmlFor="reason" className={`GM__${theme}-form-label`}>
                Razón de cancelación <span className="required">*</span>
              </label>
              <textarea
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                className={`GM__${theme}-form-textarea ${errors.reason ? 'error' : ''}`}
                placeholder="Ingrese el motivo de la cancelación..."
                rows={3}
              ></textarea>
              {errors.reason && (
                <div className={`GM__${theme}-form-error`}>{errors.reason}</div>
              )}
            </div>

            {/* Tipo de reemplazo */}
            <div className={`GM__${theme}-form-group`}>
              <p className={`GM__${theme}-form-subtitle`}>Tipo de reemplazo</p>
              
              <div className={`GM__${theme}-radio-group`}>
                <div className={`GM__${theme}-radio-option`}>
                  <input 
                    type="radio" 
                    id="none" 
                    name="replacementType" 
                    value="none" 
                    checked={formData.replacementType === 'none'}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="none">Sin reemplazo</label>
                </div>
                
                <div className={`GM__${theme}-radio-option`}>
                  <input 
                    type="radio" 
                    id="automatic" 
                    name="replacementType" 
                    value="automatic" 
                    checked={formData.replacementType === 'automatic'}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="automatic">Reemplazo automático</label>
                </div>
                
                <div className={`GM__${theme}-radio-option`}>
                  <input 
                    type="radio" 
                    id="manual" 
                    name="replacementType" 
                    value="manual" 
                    checked={formData.replacementType === 'manual'}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="manual">Seleccionar reemplazo</label>
                </div>
              </div>
            </div>

            {/* Información de reemplazo */}
            <div className={`GM__${theme}-alert info`}>
              <i className={`GM__${theme}-alert-icon`}>ℹ️</i>
              <span>{getReplacementInfo()}</span>
            </div>

            {/* Selector de vehículo de reemplazo */}
            {formData.replacementType === 'manual' && (
              <div className={`GM__${theme}-form-group`} ref={dropdownRef}>
                <label className={`GM__${theme}-form-label`}>
                  Vehículo de reemplazo <span className="required">*</span>
                </label>
                
                <div className={`GM__${theme}-dropdown`}>
                  <div 
                    className={`GM__${theme}-dropdown-toggle ${errors.replacementVehicleId ? 'error' : ''}`}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    {getSelectedVehicle() ? (
                      <span>{getSelectedVehicle().label}</span>
                    ) : (
                      <span className={`GM__${theme}-placeholder`}>Seleccione un vehículo...</span>
                    )}
                    
                    {isLoadingVehicles ? (
                      <CircularProgress size={20} />
                    ) : (
                      <span className={`GM__${theme}-dropdown-arrow`}>▼</span>
                    )}
                  </div>
                  
                  {dropdownOpen && !isLoadingVehicles && (
                    <div className={`GM__${theme}-dropdown-menu`}>
                      {availableVehicles && availableVehicles.length > 0 ? (
                        availableVehicles.map(vehicle => (
                          <div 
                            key={vehicle.id} 
                            className={`GM__${theme}-dropdown-item`}
                            onClick={() => handleVehicleSelect(vehicle)}
                          >
                            <span>{vehicle.label}</span>
                            <span className={`GM__${theme}-vehicle-type ${vehicle.type}`}>
                              {vehicle.type === 'combined' ? 'Combinado' : 'Vehículo'}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className={`GM__${theme}-dropdown-empty`}>
                          No hay vehículos disponibles
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {errors.replacementVehicleId && (
                  <div className={`GM__${theme}-form-error`}>{errors.replacementVehicleId}</div>
                )}
              </div>
            )}

            {/* Advertencia para cancelación sin reemplazo */}
            {formData.replacementType === 'none' && formData.reason && (
              <div className={`GM__${theme}-alert warning`}>
                <i className={`GM__${theme}-alert-icon`}>⚠️</i>
                <p>
                  <strong>Importante:</strong> El vehículo cancelado se colocará 
                  en primera posición el día siguiente para mantener la rotación.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`GM__${theme}-modal-footer`}>
          <button 
            className={`GM__${theme}-button secondary`}
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            className={`GM__${theme}-button danger`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={16} />
                <span>Procesando...</span>
              </>
            ) : (
              'Confirmar Cancelación'
            )}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CancelVehicleModal;